import {
    AssessmentSchedule as APIAssessmentSchedule,
    NotificationResourceType,
    NotificationType,
    ResourceNotificationSetting
} from "../../API";
import {
    AssessmentSchedule,
    Contact
} from "../../models";
import {
    Box,
    Button,
    Step,
    StepLabel,
    Stepper,
    Typography
} from "@mui/material";
import {
    Dispatch,
    Fragment,
    SetStateAction,
    useMemo,
    useRef,
    useState
} from "react";

import { AssessmentScheduleAuthorizedIdentifiersForm } from "./AssessmentScheduleAuthorizedIdentifiersForm";
import { AssessmentScheduleNotificationForm } from "./AssessmentScheduleNotificationForm";
import { AssessmentScheduleRecurrenceForm } from "./AssessmentScheduleRecurrenceForm";
import LoadingButton from "@mui/lab/LoadingButton";
import { ResourceNotificationSettingDraft } from "../../lib/dashboard/types/ResourceNotificationSettingDraft";
import { ScheduleChecker } from "../dashboard/ScheduleChecker";
import { SelectLocationsForScheduleForm } from "./SelectLocationsForScheduleForm";
import { format } from "date-fns";
import { useSnackbar } from "notistack";

type AssessmentScheduleFormProps = {
    existingAssessmentSchedule?: AssessmentSchedule;
    existingAssociatedLocationIds?: Array<string>;
    existingMissedAssessmentNotificationSetting?: ResourceNotificationSetting;
    onSubmit: (schedule: AssessmentSchedule, selectedLocationIds: Array<string>, notificationSetting: ResourceNotificationSettingDraft) => Promise<void>;
    onCancel: () => (void | Promise<void>);
    onCompleted: () => (void | Promise<void>);
    contacts: Array<Contact>;
    setIsOpenCreateContactDialog: Dispatch<SetStateAction<boolean>>;
};

enum AssessmentScheduleFormStep {
    RECURRENCE,
    AUTHORIZED_USERS,
    NOTIFICATIONS,
    LOCATION,
    COMPLETION
}

export const AssessmentScheduleForm = (props: AssessmentScheduleFormProps) => {
    const {
        existingAssociatedLocationIds,
        onCancel,
        onCompleted,
        contacts,
        setIsOpenCreateContactDialog,
        existingAssessmentSchedule,
        existingMissedAssessmentNotificationSetting,
        onSubmit
    } = props;
    const [activeStep, setActiveStep] = useState<AssessmentScheduleFormStep>(AssessmentScheduleFormStep.RECURRENCE);
    const [assessmentSchedule, setAssessmentSchedule] = useState(existingAssessmentSchedule);
    const [associatedLocationIds, setAssociatedLocationIds] = useState(existingAssociatedLocationIds ?? []);

    const [missedAssessmentNotificationSetting, setMissedAssessmentNotificationSetting] = useState<ResourceNotificationSettingDraft>(existingMissedAssessmentNotificationSetting ?? {
        notificationType: NotificationType.MISSED_ASSESSMENT,
        resourceType: NotificationResourceType.ASSESSMENT_SCHEDULE,
        enabled: false,
        recipients: []
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [authorizedContactIds, setAuthorizedContactIds] = useState<Array<string>>(existingAssessmentSchedule?.authorizedIdentifiers ?? []);
    const prevAuthorizedContactIds = useRef<Array<string>>(authorizedContactIds);
    const snackbar = useSnackbar();

    const scheduleChecker = useMemo<ScheduleChecker | undefined>(() => {
        if (!assessmentSchedule) {
            return undefined;
        }
        return new ScheduleChecker(assessmentSchedule as APIAssessmentSchedule);
    }, [assessmentSchedule]);

    const firstScheduledDateString = useMemo<string | undefined>(() => {
        if (!scheduleChecker) {
            return undefined;
        }
        const scheduledDate = scheduleChecker.getFirstScheduledDate();
        return scheduledDate ? format(scheduledDate, "yyyy-MM-dd") : undefined;
    }, [scheduleChecker]);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Stepper activeStep={activeStep}>
                <Step>
                    <StepLabel>Recurrence</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Authorized Users</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Notifications</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Locations</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Done</StepLabel>
                </Step>
            </Stepper>
            {activeStep === AssessmentScheduleFormStep.RECURRENCE && (
                <Fragment>
                    <AssessmentScheduleRecurrenceForm
                        initialAssessmentSchedule={assessmentSchedule}
                        onSubmit={(schedule: AssessmentSchedule) => {
                            setAssessmentSchedule(schedule);
                            setActiveStep(1);
                        }}
                        onCancel={onCancel}
                        submitLabel="Next"
                    />
                </Fragment>
            )}
            {activeStep === AssessmentScheduleFormStep.AUTHORIZED_USERS && (
                <>
                    <AssessmentScheduleAuthorizedIdentifiersForm
                        contacts={contacts}
                        authorizedIdentifiers={authorizedContactIds}
                        setAuthorizedIdentifiers={setAuthorizedContactIds}
                        setIsOpenCreateContactDialog={setIsOpenCreateContactDialog}
                    />
                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Button
                            onClick={() => setActiveStep(AssessmentScheduleFormStep.RECURRENCE)}
                            variant="outlined"
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={async () => {
                                setAssessmentSchedule(prev => ({
                                    ...prev!,
                                    authorizedIdentifiers: authorizedContactIds.map((identifier) => identifier.trim())
                                }));
                                setActiveStep(AssessmentScheduleFormStep.NOTIFICATIONS);
                            }}
                            disabled={authorizedContactIds.length === 0}
                        >
                            Next
                        </Button>
                    </Box>
                </>
            )}
            {activeStep === AssessmentScheduleFormStep.NOTIFICATIONS && (
                <Fragment>
                    <AssessmentScheduleNotificationForm
                        authorizedContactIds={authorizedContactIds}
                        contacts={contacts}
                        setIsOpenCreateContactDialog={setIsOpenCreateContactDialog}
                        notificationSetting={missedAssessmentNotificationSetting}
                        setNotificationSetting={setMissedAssessmentNotificationSetting}
                    />
                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Button
                            onClick={() => setActiveStep(AssessmentScheduleFormStep.AUTHORIZED_USERS)}
                            variant="outlined"
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setActiveStep(AssessmentScheduleFormStep.LOCATION);
                            }}
                        >
                            Next
                        </Button>
                    </Box>
                </Fragment>
            )}
            {activeStep === AssessmentScheduleFormStep.LOCATION && (
                <Fragment>
                    <SelectLocationsForScheduleForm
                        selectedLocationIds={associatedLocationIds}
                        setSelectedLocationIds={setAssociatedLocationIds}
                        existingAssociatedLocationIds={existingAssociatedLocationIds}
                    />
                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Button
                            onClick={() => setActiveStep(AssessmentScheduleFormStep.NOTIFICATIONS)}
                            variant="outlined"
                        >
                            Back
                        </Button>
                        <LoadingButton
                            variant="contained"
                            onClick={async () => {
                                try {
                                    setIsSubmitting(true);
                                    if (onSubmit) {
                                        await onSubmit(assessmentSchedule!, associatedLocationIds, missedAssessmentNotificationSetting);
                                    }
                                    setActiveStep(AssessmentScheduleFormStep.COMPLETION);
                                } catch (error) {
                                    snackbar.enqueueSnackbar("Error submitting assessment schedule", { variant: "error" });
                                } finally {
                                    setIsSubmitting(false);
                                }
                            }}
                            disabled={associatedLocationIds.length === 0}
                            loading={isSubmitting}
                        >
                            Submit
                        </LoadingButton>
                    </Box>
                </Fragment>
            )}
            {activeStep === AssessmentScheduleFormStep.COMPLETION && (
                <Fragment>
                    <Box>
                        <Typography>
                            Your first assessment will be scheduled on {firstScheduledDateString ?? "N/A"}.
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Button
                            sx={{ ml: "auto" }}
                            variant="contained"
                            onClick={async () => onCompleted?.()}
                        >
                            Done
                        </Button>
                    </Box>
                </Fragment>
            )}
        </Box>
    );
};